/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * TypeScript 辅助工具
 * 用于处理开发过程中的类型问题
 */

/**
 * 标记变量为已使用（避免 TypeScript 未使用变量警告）
 * @param vars 要标记的变量
 */
export function markAsUsed(...vars: any[]): void {
  // 这个函数的目的是让 TypeScript 认为变量被使用了
  // 在生产环境中，这些代码会被优化掉
  if (import.meta.env.DEV) {
    vars.forEach(() => {
      // 空操作，只是为了"使用"变量
    })
  }
}

/**
 * 开发环境下忽略未使用变量的装饰器
 * @param target 目标对象
 * @param propertyKey 属性键
 */
export function ignoreUnused(target: any, propertyKey: string): void {
  if (import.meta.env.DEV) {
    // 在开发环境下标记为已使用
    markAsUsed(target, propertyKey)
  }
}

/**
 * 开发环境下的调试日志
 * @param message 日志消息
 * @param data 可选的数据
 */
export function devLog(message: string, data?: any): void {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.log(`[DEV] ${message}`, data)
    } else {
      console.log(`[DEV] ${message}`)
    }
  }
}

/**
 * 开发环境下的警告日志
 * @param message 警告消息
 * @param data 可选的数据
 */
export function devWarn(message: string, data?: any): void {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.warn(`[DEV] ${message}`, data)
    } else {
      console.warn(`[DEV] ${message}`)
    }
  }
}

/**
 * 开发环境下的错误日志
 * @param message 错误消息
 * @param error 可选的错误对象
 */
export function devError(message: string, error?: any): void {
  if (import.meta.env.DEV) {
    if (error !== undefined) {
      console.error(`[DEV] ${message}`, error)
    } else {
      console.error(`[DEV] ${message}`)
    }
  }
}

/**
 * 类型断言辅助函数
 * @param value 要断言的值
 * @param message 断言失败时的消息
 */
export function assert(value: any, message: string): asserts value {
  if (!value) {
    throw new Error(`Assertion failed: ${message}`)
  }
}

/**
 * 安全的 JSON 解析
 * @param json JSON 字符串
 * @param fallback 解析失败时的回退值
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T
  } catch (error) {
    devWarn('JSON parse failed', { json, error })
    return fallback
  }
}

/**
 * 安全的 JSON 字符串化
 * @param value 要字符串化的值
 * @param fallback 字符串化失败时的回退值
 */
export function safeJsonStringify(value: any, fallback: string = '{}'): string {
  try {
    return JSON.stringify(value)
  } catch (error) {
    devWarn('JSON stringify failed', { value, error })
    return fallback
  }
}

/**
 * 延迟执行函数
 * @param ms 延迟毫秒数
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * 防抖函数
 * @param func 要防抖的函数
 * @param wait 等待时间
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    
    timeout = window.setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * 节流函数
 * @param func 要节流的函数
 * @param limit 限制时间
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}